import { useLayoutEffect, useRef } from 'react'
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom'

/**
 * Прокрутка как в приложении: новый экран открывается сверху, а «назад»
 * (стрелка экрана, системная кнопка, жест) возвращает туда, где ушли, — к тому
 * же товару в ленте. Позиция живёт у записи истории (`location.key`), поэтому
 * у двух заходов на одну страницу она своя. Смена параметров на той же
 * странице (вкладка заказов) прокрутку не трогает.
 *
 * Браузер сам восстанавливает прокрутку раньше, чем страница дорисуется, и
 * промахивается — его восстановление выключаем. Позиции лежат и в
 * sessionStorage: перезагрузка страницы их не теряет.
 */
export const SCROLL_STORAGE_KEY = 'dorixona.scroll'

/** Сколько кадров ждать, пока вернувшаяся страница дорастёт до своей позиции. */
export const RESTORE_FRAMES = 60

/** Сколько последних позиций хранить: история длиннее назад не листается. */
const KEEP = 50

const positions = new Map<string, number>(readStored())

function readStored(): [string, number][] {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(SCROLL_STORAGE_KEY) ?? '{}')
    return Object.entries(stored as Record<string, unknown>).filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number',
    )
  } catch {
    return []
  }
}

function store() {
  try {
    const recent = [...positions].slice(-KEEP)
    sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(Object.fromEntries(recent)))
  } catch {
    // Хранилище закрыто — позиции останутся только до перезагрузки.
  }
}

/**
 * Вернуть прокрутку. Данные вернувшейся страницы могут ещё догружаться, и
 * она короче нужного — пробуем каждый кадр, пока не дойдём. Палец коснулся
 * экрана — перестаём: прокрутку у человека не отнимаем.
 */
function restore(top: number): () => void {
  let frame = 0
  let left = RESTORE_FRAMES
  const stop = () => {
    cancelAnimationFrame(frame)
    window.removeEventListener('touchstart', stop)
    window.removeEventListener('wheel', stop)
  }
  const attempt = () => {
    window.scrollTo(0, top)
    left -= 1
    if (Math.abs(window.scrollY - top) <= 1 || left <= 0) {
      stop()
      return
    }
    frame = requestAnimationFrame(attempt)
  }
  window.addEventListener('touchstart', stop, { passive: true })
  window.addEventListener('wheel', stop, { passive: true })
  attempt()
  return stop
}

export function useScrollMemory(): void {
  const location = useLocation()
  const navigationType = useNavigationType()
  const pathname = useRef(location.pathname)

  useLayoutEffect(() => {
    window.history.scrollRestoration = 'manual'
    window.addEventListener('pagehide', store)
    return () => {
      window.removeEventListener('pagehide', store)
    }
  }, [])

  // location и тип перехода меняются вместе — один раз на переход.
  useLayoutEffect(() => {
    const { key } = location
    const samePage = pathname.current === location.pathname
    pathname.current = location.pathname

    let stopRestoring = () => undefined as void
    if (navigationType === NavigationType.Pop) stopRestoring = restore(positions.get(key) ?? 0)
    else if (!samePage) window.scrollTo(0, 0)

    const remember = () => {
      positions.delete(key)
      positions.set(key, window.scrollY)
    }
    window.addEventListener('scroll', remember, { passive: true })
    return () => {
      stopRestoring()
      window.removeEventListener('scroll', remember)
    }
  }, [location, navigationType])
}

/** Для тестов: забыть позиции. */
export function resetScrollMemory(): void {
  positions.clear()
}
